import { Component, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DeliveryWindowForm, DeliveryWindowVM } from '@geofences/delivery-windows/delivery-window';
import { DeliveryWindowFormDialog } from '@geofences/delivery-windows/delivery-window-form-dialog/delivery-window-form-dialog';
import { ButtonModule } from 'primeng/button';
import { firstValueFrom } from 'rxjs';

import { DeliveryWindowsService } from '@core/services/delivery-windows';
import { GeofenceService } from '@core/services/geofence';

import { DeliveryWindowCreateLogic } from '../delivery-windows-create-logic';

@Component({
  selector: 'app-delivery-windows-header',
  imports: [ButtonModule, DeliveryWindowFormDialog],
  templateUrl: './delivery-windows-header.html',
  styleUrl: './delivery-windows-header.scss'
})
export class DeliveryWindowsHeader {
  private readonly deliveryWindowsAPIs = inject(DeliveryWindowsService);
  private readonly geofenceAPIs = inject(GeofenceService);

  private readonly createLogic = inject(DeliveryWindowCreateLogic);

  readonly id = input.required<string>();

  readonly showFormDialog = signal(false);
  readonly dialogMode = signal<'create' | 'edit'>('create');
  readonly selectedDeliveryWindow = signal<DeliveryWindowVM | undefined>(undefined);
  readonly saving = signal(false);

  readonly deliveryWindows = toSignal(this.deliveryWindowsAPIs.deliveryWindows$, {
    initialValue: []
  });

  private async getGeofence(): Promise<{ geofenceId: string; ianaZone: string } | null> {
    const id = this.id();
    if (!id) return null;

    try {
      const response = await firstValueFrom(this.geofenceAPIs.getById(id));
      return { geofenceId: response.data.geofenceId, ianaZone: response.data.ianaZone };
    } catch {
      return null;
    }
  }

  onCreateDeliveryWindow(): void {
    this.dialogMode.set('create');
    this.selectedDeliveryWindow.set(undefined);
    this.showFormDialog.set(true);
  }

  async submitDeliveryWindowData(formData: DeliveryWindowForm): Promise<void> {
    const geofence = await this.getGeofence();
    const selected = this.selectedDeliveryWindow();

    if (!geofence) return;

    this.saving.set(true);
    try {
      if (this.dialogMode() === 'create') {
        await this.createLogic.create(formData, geofence);
      } else {
        if (!selected?.deliveryWindowId) {
          throw new Error('Missing deliveryWindowId');
        }

        await this.createLogic.edit(formData, geofence, selected.deliveryWindowId);
      }

      this.closeDialog();
    } finally {
      this.saving.set(false);
    }
  }

  cancelSubmission(): void {
    this.closeDialog();
  }

  dialogVisibility(visible: boolean): void {
    this.showFormDialog.set(visible);
  }

  private closeDialog(): void {
    this.showFormDialog.set(false);
    this.selectedDeliveryWindow.set(undefined);
  }
}
